import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Badge } from '~/components/ui/badge';
import { useCreateGradingTemplate, useUpdateGradingTemplate } from './useGradingTemplateMutations';

interface CategoryRow {
  id?: string;
  name: string;
  weight: string; // string so the input doesn't fight with empty state
  dropLowest: string;
}

interface ExistingTemplate {
  id: string;
  name: string;
  description: string | null;
  categories: ReadonlyArray<{
    id: string;
    name: string;
    weight: number;
    dropLowest: number;
    position: number;
  }>;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string;
  template: ExistingTemplate | null; // null = create mode
  onComplete: () => void;
}

const blankRow = (): CategoryRow => ({ name: '', weight: '', dropLowest: '0' });

export default function GradingTemplateDialog({ open, onOpenChange, schoolId, template, onComplete }: Props) {
  const [createTemplate] = useCreateGradingTemplate();
  const [updateTemplate] = useUpdateGradingTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rows, setRows] = useState<CategoryRow[]>([blankRow()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setSaving(false);
      if (template) {
        setName(template.name);
        setDescription(template.description || '');
        setRows(
          [...template.categories]
            .sort((a, b) => a.position - b.position)
            .map((c) => ({
              id: c.id,
              name: c.name,
              weight: String(c.weight),
              dropLowest: String(c.dropLowest),
            }))
        );
      } else {
        setName('');
        setDescription('');
        setRows([blankRow()]);
      }
    }
  }, [open, template]);

  const weightTotal = rows.reduce((sum, r) => sum + (parseFloat(r.weight) || 0), 0);

  const updateRow = (idx: number, patch: Partial<CategoryRow>) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const removeRow = (idx: number) => setRows((rs) => rs.filter((_, i) => i !== idx));
  const addRow = () => setRows((rs) => [...rs, blankRow()]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      setError('Template name is required');
      return;
    }
    const filledRows = rows.filter((r) => r.name.trim());
    if (filledRows.some((r) => !r.weight || isNaN(parseFloat(r.weight)))) {
      setError('Every category needs a numeric weight');
      return;
    }
    setSaving(true);
    setError(null);

    const categories = filledRows.map((r, idx) => ({
      ...(r.id ? { id: r.id } : {}),
      name: r.name.trim(),
      weight: parseFloat(r.weight),
      dropLowest: parseInt(r.dropLowest, 10) || 0,
      position: idx,
    }));

    try {
      if (template) {
        const result = await updateTemplate({
          variables: {
            input: {
              id: template.id,
              name: name.trim(),
              description: description.trim() || null,
              categories,
            },
          },
        });
        const userErrors = result.data?.updateGradingTemplate?.userErrors;
        if (userErrors?.length) {
          setError(userErrors.map((e: any) => e.message).join(', '));
          return;
        }
      } else {
        const result = await createTemplate({
          variables: {
            input: {
              schoolId,
              name: name.trim(),
              description: description.trim() || null,
              categories,
            },
          },
        });
        const userErrors = result.data?.createGradingTemplate?.userErrors;
        if (userErrors?.length) {
          setError(userErrors.map((e: any) => e.message).join(', '));
          return;
        }
      }
      onComplete();
    } catch (e: any) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [name, description, rows, template, schoolId, createTemplate, updateTemplate, onComplete]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Grading Template' : 'New Grading Template'}</DialogTitle>
          <DialogDescription>
            Templates are reusable. Classes pick one to seed their own gradebook categories. Edits here don't affect classes that already picked this template.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="tname">Name</Label>
            <Input id="tname" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Standard High School" />
          </div>
          <div>
            <Label htmlFor="tdesc">Description (optional)</Label>
            <Input id="tdesc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Categories</Label>
              <Badge variant={weightTotal === 100 ? 'default' : 'secondary'}>
                Total: {weightTotal.toFixed(2)}%
              </Badge>
            </div>
            <div className="space-y-2">
              {rows.map((r, idx) => (
                <div key={r.id ?? `new-${idx}`} className="grid grid-cols-[2fr_1fr_1fr_auto] gap-2 items-end">
                  <div>
                    {idx === 0 && <Label className="text-xs">Name</Label>}
                    <Input
                      aria-label={`Category name ${idx + 1}`}
                      value={r.name}
                      onChange={(e) => updateRow(idx, { name: e.target.value })}
                      placeholder="Tests"
                    />
                  </div>
                  <div>
                    {idx === 0 && <Label className="text-xs">Weight %</Label>}
                    <Input
                      aria-label={`Category weight ${idx + 1}`}
                      type="number"
                      value={r.weight}
                      onChange={(e) => updateRow(idx, { weight: e.target.value })}
                      placeholder="40"
                    />
                  </div>
                  <div>
                    {idx === 0 && <Label className="text-xs">Drop N lowest</Label>}
                    <Input
                      aria-label={`Category drop lowest ${idx + 1}`}
                      type="number"
                      value={r.dropLowest}
                      onChange={(e) => updateRow(idx, { dropLowest: e.target.value })}
                      min="0"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove category ${idx + 1}`}
                    onClick={() => removeRow(idx)}
                    disabled={rows.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={addRow} className="mt-2">
              <Plus className="h-4 w-4 mr-1" />
              Add Category
            </Button>
            {weightTotal !== 100 && (
              <p className="text-xs text-muted-foreground mt-2">
                Weights don't sum to 100. That's allowed, but most templates total 100%.
              </p>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : template ? 'Save Changes' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
